import { createContext, useContext } from 'react'

import type { DataTableContextValue } from './DataTable.types'

// Create the context with proper generic handling
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const DataTableContext = createContext<DataTableContextValue<any> | null>(null)

// Context provider component
export const DataTableProvider = <TData,>({
  children,
  value
}: {
  children: React.ReactNode
  value: DataTableContextValue<TData>
}) => {
  return <DataTableContext.Provider value={value}>{children}</DataTableContext.Provider>
}

// Hook to use the context
export const useDataTableContext = <TData,>(): DataTableContextValue<TData> => {
  const context = useContext(DataTableContext) as DataTableContextValue<TData>

  if (!context) {
    throw new Error(
      'useDataTableContext must be used within a DataTable component. ' +
        'Make sure you are using table sub-components inside a DataTable.'
    )
  }

  return context
}

// Typed hooks for specific context properties
export const useDataTable = <TData,>() => {
  const { table } = useDataTableContext<TData>()
  return table
}

export const useDataTableFeatures = () => {
  const { features } = useDataTableContext()
  return features
}

export const useDataTableState = () => {
  const { state, setState } = useDataTableContext()
  return { state, setState }
}

export const useDataTableResponsive = () => {
  const { responsive } = useDataTableContext()
  return responsive
}
